/**
 * DateInput — 숫자만 쳐도 「YYYY-MM-DD」로 나뉘는 날짜 칸 (2026-10-07 지도사 「19710101 이런 식으로 숫자로 쓰면 알아서 - 구분되게」)
 *
 *  왜: 브라우저 기본 date 칸은 연도 칸이 6자리까지 받아 「196107-12-일」처럼 깨지고, 생년월일 · 설립일은 달력보다 키보드가 빠르다.
 *  - 입력: 숫자만 남겨 8자리까지 → 4 · 2 · 2 로 하이픈. 붙여넣기 「1971.01.01」「1971/1/1」「71-01-01」도 정리.
 *  - 값(onChange)은 완성된 「YYYY-MM-DD」(실제 있는 날짜)일 때와 비웠을 때만 올린다 — 쓰는 중 값이 저장되지 않게.
 *  - 오른쪽 달력 단추 = 기본 date 고르기(showPicker) — 마감일처럼 달력이 편한 칸도 그대로 쓸 수 있다.
 */
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  value: string | null | undefined;
  onChange: (v: string) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  placeholder?: string;
  min?: string;
  max?: string;
  'aria-label'?: string;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 실제 있는 날짜인가 (2월 30일 · 13월 막기) */
export function validYmd(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const y = +m[1], mo = +m[2], d = +m[3];
  if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || d < 1) return false;
  return d <= new Date(y, mo, 0).getDate();
}

/** 친 글 → 보이는 글 (숫자 8자리까지 4-2-2) · 구분자 있는 붙여넣기는 칸별로 */
export function maskYmd(raw: string): string {
  const t = String(raw || '').trim();
  const parts = t.split(/[.\-/\s년월일]+/).filter(Boolean);
  // 칸 나눔 붙여넣기(「1971.1.1」「1971/1/1」「71-01-01」)만 칸별로 — 「1971-01-1」(쓰거나 지우는 중)은 숫자 그대로 두어야 지우기가 막히지 않는다
  if (parts.length === 3 && parts.every((p) => /^\d+$/.test(p)) && (/[./\s년월일]/.test(t) || /^\d{2}-/.test(t))) {
    let y = parts[0];
    if (y.length === 2) y = (+y > 30 ? '19' : '20') + y;   // 「71-01-01」 → 1971
    if (y.length === 4 && parts[1].length <= 2 && parts[2].length <= 2) return `${y}-${pad2(+parts[1])}-${pad2(+parts[2])}`;
  }
  const d = t.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}

export default function DateInput({ value, onChange, style, disabled, placeholder, min, max, ...rest }: Props) {
  const v = String(value || '').slice(0, 10);
  const [text, setText] = useState(v);
  const pick = useRef<HTMLInputElement>(null);
  // 밖에서 값이 바뀌면(업체 불러오기 · 달력) 보이는 글도 따라간다 — 쓰는 중(미완성)에는 값을 올리지 않으므로 v 가 안 바뀌어 덮이지 않는다
  useEffect(() => { setText(v); }, [v]);

  const bad = text.length === 10 && !validYmd(text);
  const set = (raw: string) => {
    const m = maskYmd(raw);
    setText(m);
    if (!m) onChange('');
    else if (validYmd(m)) onChange(m);
  };
  const openPicker = () => {
    const el = pick.current;
    if (!el || disabled) return;
    try { (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch { el.focus(); }
  };

  return (
    <span style={{ position: 'relative', display: 'block' }}>
      <input type="text" inputMode="numeric" autoComplete="off" disabled={disabled} aria-label={rest['aria-label']}
        value={text} placeholder={placeholder || 'YYYYMMDD'}
        onChange={(e) => set(e.target.value)}
        onBlur={() => { if (text && !validYmd(text)) setText(v); }}   // 미완성 · 없는 날짜는 저장된 값으로 되돌린다
        style={{ ...style, width: '100%', boxSizing: 'border-box', paddingRight: 34, ...(bad ? { borderColor: '#DC2626' } : {}) }} />
      <button type="button" tabIndex={-1} disabled={disabled} onClick={openPicker} title="달력에서 고르기" aria-label="달력에서 고르기"
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 24, height: 24, padding: 0, border: 0, background: 'transparent', cursor: disabled ? 'default' : 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
      </button>
      <input ref={pick} type="date" tabIndex={-1} aria-hidden="true" value={validYmd(v) ? v : ''} min={min} max={max}
        onChange={(e) => { setText(e.target.value); onChange(e.target.value); }}
        style={{ position: 'absolute', right: 6, bottom: 0, width: 24, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0 }} />
    </span>
  );
}
